import { useState, useEffect } from 'react'
import { Menu } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import MainTopBar from '@/components/common/bar/MainTopBar'
// import ChatMessageInput from '@/components/chat/ChatMessageInput'
import TypeCheckOverlay from '@/components/chat/TypeCheckOverlay'
import ChatTopicGrid from '@/components/chat/ChatTopicGrid'
import ChatHistorySidebar from '@/components/chat/ChatHistorySidebar'
import { useGetActiveConsultation, useUserConsultBlockList } from '@/hooks/queries/chat'
import { useUserStore } from '@/store/userStore'
import { useChatStore } from '@/store/chatStore'
import { useTypeTestStore } from '@/store/typeTestStore'
import Toast from '@/components/common/toast/ToastMessage'
import PullToRefresh from '@/components/common/refresh/PullToRefresh'
import { getTakeoverNoteStatus } from '@/api/chatApi'
import { useUserInfoQuery } from '@/hooks/queries/user'
import { openNativeChatPage } from '@/utils/webViewBridge'

// 상담 블록 관련 토스트 문구 타입
type ConsultationBlockToastType = 'ACTIVE_CONSULTATION_EXISTS' | 'TAKEOVER_IN_PROGRESS'

export default function ChatPage() {
  const navigate = useNavigate()
  const [selectedTopic, setSelectedTopic] = useState<number | null>(null)
  // const [message, setMessage] = useState('')
  // const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [showDisabledToast, setShowDisabledToast] = useState<ConsultationBlockToastType | null>(null)

  const { userInfo } = useUserStore()
  const { data: userInfoQueryData } = useUserInfoQuery(userInfo.user_id)
  const { setTypeResultFrom } = useTypeTestStore()
  const { data: userConsultBlockList } = useUserConsultBlockList(userInfo.user_id)
  const { setRecentChatPage } = useChatStore()
  const { data: activeConsultation, refetch: refetchActiveConsultation } = useGetActiveConsultation()

  const handleTopicSelect = async (block_id: number) => {
    // 상담 진행중일 경우 -진행중 상담 블록을 클릭한 경우 : 진행 중 상담 채팅방으로 이동
    if (activeConsultation && activeConsultation.block_type_id === block_id) {
      const consultationId = activeConsultation.consultation_id
      if (consultationId !== null) {
        // URL 네비게이션 대신 브릿지 사용
        openNativeChatPage(consultationId, '/chat')
        return
      }
    }

    //상담 진행중일 경우 -진행중 상담 블록 외 클릭한 경우 : 토스트 문구
    if (activeConsultation?.consultation_id) {
      setShowDisabledToast('ACTIVE_CONSULTATION_EXISTS')
      return
    }

    // 인수인계 확인
    const response = await getTakeoverNoteStatus(block_id)
    if (!response || !response.proceed) {
      setShowDisabledToast('TAKEOVER_IN_PROGRESS')
      return
    }

    setSelectedTopic(block_id)
    navigate(`/chat-prepare`, { state: { block_id } })
  }

  // const handleSendMessage = () => {
  //   if (message.trim()) {
  //     navigate(`/chat/new`, { state: { message }, replace: true })

  //     setMessage('')
  //     if (textareaRef.current) {
  //       textareaRef.current.style.height = 'auto'
  //     }
  //   }
  // }

  const handleStartTypeCheck = () => {
    setTypeResultFrom('chat')
    navigate('/type-test')
  }

  // const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
  //   if (e.key === 'Enter' && !e.shiftKey) {
  //     e.preventDefault()
  //     handleSendMessage()
  //   }
  // }

  // const isSendButtonActive = message.trim() !== ''

  useEffect(() => {
    setRecentChatPage(null)
  }, [setRecentChatPage])

  // 상담 종료 후 다시
  useEffect(() => {
    refetchActiveConsultation()
  }, [refetchActiveConsultation])

  return (
    <>
      <MainTopBar
        leftSide={
          <div className='flex items-center gap-2'>
            <h1 className='text-header-title font-semibold text-text-header'>상담</h1>
          </div>
        }
        rightSide={
          <button
            onClick={() => setIsSidebarOpen(true)}
            className='w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors'
          >
            <Menu className='w-5 h-5 text-amber-900' />
          </button>
        }
      />
      <PullToRefresh
        onRefresh={() => {
          window.location.reload()
        }}
      >
        <div className='min-h-screen'>
          {/* 메인 콘텐츠 영역 */}
          <div className={`px-3  pt-3 `}>
            <h2 className='text-xl font-bold text-gray-800 my-4 text-center mb-9'>
              너만의 성장 여정
              <br />
              시작해볼까?
            </h2>

            {/* 상담 주제 그리드 */}
            <ChatTopicGrid
              selectedTopic={selectedTopic}
              onTopicSelect={id => {
                handleTopicSelect(id)
              }}
              topics={userConsultBlockList || []}
              activeTopic={activeConsultation?.block_type_id ?? undefined}
            />
          </div>

          {/* 채팅 입력칸 */}
          {/* <ChatMessageInput
          showTypeCheckOverlay={!userInfo.type16_id}
          textareaRef={textareaRef as React.RefObject<HTMLTextAreaElement>}
          message={message}
          setMessage={setMessage}
          handleKeyPress={handleKeyPress}
          handleSendMessage={handleSendMessage}
          isSendButtonActive={isSendButtonActive}
          placeholderText={'상담 내용을 입력하거나 주제를 선택하세요.'}
          isChatMainPage={true}
        /> */}

          {/* 대화 리스트 사이드바 */}
          {userInfoQueryData?.data.type16_id && (
            <ChatHistorySidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
          )}

          {/* 타입 검사 유도 오버레이 */}
          {!userInfoQueryData?.data.type16_id && <TypeCheckOverlay onStartTest={handleStartTypeCheck} />}
        </div>
      </PullToRefresh>
      {showDisabledToast === 'ACTIVE_CONSULTATION_EXISTS' && (
        <Toast
          message='상담 중에는 주제를 선택할 수 없습니다.'
          isVisible={true}
          onClose={() => setShowDisabledToast(null)}
        />
      )}
      {showDisabledToast === 'TAKEOVER_IN_PROGRESS' && (
        <Toast
          message='지난 상담을 정리하고 있습니다. 잠시 후에 다시 시도해주세요.'
          isVisible={true}
          onClose={() => setShowDisabledToast(null)}
        />
      )}
    </>
  )
}
